import { LogOut, User, ChevronDown, Bell } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import { initials } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

const ROL_LABEL: Record<string, string> = {
  ADMIN:    'Administrador',
  SOPORTE:  'Soporte TI',
  COMPRAS:  'Compras',
  GUARDIA:  'Portería',
  PEOPLE:   'Gestión Humana',
};

export function Topbar({ title, subtitle }: { title: string; subtitle?: string }) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b bg-background/80 backdrop-blur-md px-6">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-tight text-foreground truncate">{title}</h1>
        {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
      </div>

      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" aria-label="Notificaciones">
          <Bell className="h-4 w-4" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 rounded-full px-1.5 py-1 hover:bg-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Menú de usuario"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback>{initials(user?.username)}</AvatarFallback>
              </Avatar>
              <div className="hidden sm:flex flex-col items-start text-xs leading-tight pr-1">
                <span className="font-semibold text-foreground">{user?.username}</span>
                <span className="text-muted-foreground">{ROL_LABEL[user?.rol ?? ''] ?? user?.rol}</span>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal normal-case tracking-normal text-xs">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-semibold text-foreground">{user?.username}</p>
                <p className="text-xs leading-none text-muted-foreground">{user?.email ?? 'sin correo'}</p>
                <Badge variant="info" className="mt-2 w-fit">
                  {ROL_LABEL[user?.rol ?? ''] ?? user?.rol}
                </Badge>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/perfil')}>
              <User className="h-4 w-4" />
              <span>Mi perfil</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => {
                logout();
                navigate('/login', { replace: true });
              }}
              className="text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              <LogOut className="h-4 w-4" />
              <span>Cerrar sesión</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
